import { create } from "zustand";
import {
  ChartIndicators,
  ChartIndicator,
  ChartIndicatorField,
  ChartIndicatorsStore,
} from "./types";
import { getStoredIndicators, saveIndicators } from "./storage";
import { createDebouncedSave } from "@/lib/utils/debouncedPersist";

const debouncedIndicatorsSave = createDebouncedSave(saveIndicators);

/** Immediately persist any pending debounced indicators write. */
export const flushIndicatorsSave = debouncedIndicatorsSave.flush;

export const useChartIndicatorsStore = create<ChartIndicatorsStore>(
  (set, get) => ({
    indicators: getStoredIndicators() || { chartIndicators: null },

    updateIndicator: <K extends ChartIndicatorField>(
      field: K,
      value: ChartIndicators[K]
    ) => {
      const currentIndicators = get().indicators;
      const updatedIndicators = {
        ...currentIndicators,
        [field]: value,
      };

      // Update store
      set({ indicators: updatedIndicators });

      // Save to localStorage
      debouncedIndicatorsSave.schedule(updatedIndicators);
    },

    getIndicator: <K extends ChartIndicatorField>(
      field: K
    ): ChartIndicators[K] => {
      return get().indicators[field];
    },

    loadIndicators: () => {
      const indicators = getStoredIndicators() || { chartIndicators: null };
      set({ indicators });
    },

    addWindow: (indicator: ChartIndicator): ChartIndicator => {
      const currentIndicators = get().indicators;
      const currentList = currentIndicators.chartIndicators || [];

      // Generate ID if not provided
      const id = indicator.id || `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Add ID to indicator
      const indicatorWithId = {
        ...indicator,
        id: id,
      };

      // Add window to list
      const updatedList = [...currentList, indicatorWithId];
      const updatedIndicators = {
        ...currentIndicators,
        chartIndicators: updatedList,
      };

      // Update store
      set({ indicators: updatedIndicators });

      // Save to localStorage
      debouncedIndicatorsSave.schedule(updatedIndicators);

      // Return the persisted indicator (id may differ from the input).
      return indicatorWithId;
    },

    deleteWindow: (windowId: string) => {
      const currentIndicators = get().indicators;
      const currentList = currentIndicators.chartIndicators;

      if (!currentList) return;

      // Filter out window with the specified id
      const updatedList = currentList.filter(
        (window) => window.id !== windowId
      );

      // If list is empty, set to null, otherwise update
      const updatedIndicators = {
        ...currentIndicators,
        chartIndicators: updatedList.length === 0 ? null : updatedList,
      };

      // Update store
      set({ indicators: updatedIndicators });

      // Save to localStorage
      debouncedIndicatorsSave.schedule(updatedIndicators);
    },
  })
);
